import classNames from 'classnames'
import { BarChart, LineChart } from 'plume-ui'
import { House, Mobile, Pod } from 'components/icons'
import { Paper } from 'components/paper'
import { salesforcePortalProducts } from 'constants/salesforce.constants'
import { supportEmails } from 'constants/support-emails'
import { isTerminated } from 'helpers/company-helpers'
import { observer } from 'mobx-react-lite'
import { CollapseCard, Divider, Heading, Status } from 'plume-ui'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useStores } from 'utils/hooks/useStores'
import { EmptyHomesOrError } from './empty-homes-or-error/empty-homes-or-error'
import style from './home-data.css'
import { InitialOpeningHomes } from './initial-opening-homes/initial-opening-homes'
import moment from 'moment'
import { getMonth } from 'helpers/bar-chart.helpers'
import _ from 'lodash'

export const HomeData = observer(() => {
  const { t } = useTranslation()
  const appStore = useStores()
  const { authStore } = appStore
  const { currentUser } = authStore

  const getSupportMailAddress = () => {
    const { company } = currentUser

    const areAnyPortalProductsPurchased =
      company?.productsSummary?.purchased &&
      !!Object.keys(company?.productsSummary?.purchased).find(p =>
        salesforcePortalProducts.includes(p),
      )

    const supportEmail =
      areAnyPortalProductsPurchased || company.isChannelPartnerPortalCompany
        ? supportEmails.PARTNER_SUPPORT
        : company.ownerEmail

    return supportEmail
  }

  const toggle = (name: 'locations' | 'nodes' | 'devices') =>
    appStore.homeStore.toggleCollapse(name)

  const formatNumber = (value: number) =>
    value < 1000000
      ? value < 10000
        ? value.toLocaleString('en')
        : `${(value / 1000).toFixed(1)}K`
      : `${(value / 1000000).toFixed(1)}M`

  const getXAxisTickInterval = (pointsCount: number, maxTicks = 6) => {
    if (pointsCount <= 10) return 0 // show all
    return Math.ceil(pointsCount / maxTicks)
  }

  const {
    data,
    currentMonthActiveHouseholds,
    currentMonthActiveNodes,
    currentMonthActiveDevices,
    initialOpen,
    handleInitialOpen,
    serverError,
  } = appStore.homeStore

  const noAccess = serverError === 'GraphQL error: You do not have access to this partner.'
  const mailAddress = getSupportMailAddress()
  const { isMobile } = appStore.uiStore

  const isTrialExpiredOrTerminated = !currentUser?.isEmployee && isTerminated(currentUser?.company)
  const noHomeData = data.emptyHomes

  const renderHomeData = () => {
    return (
      <div className={style.dataContainer}>
        <div className={style.col}>
          <Paper
            title={t('home.locations')}
            name="locations"
            icon={<House />}
            toggle={toggle}
            isMobile={isMobile}
            isExpanded={data.locations.isExpanded}
          >
            <div className={style.paperContentWrapper}>
              <div className={style.stats}>
                <span className={style.icon}>
                  <Status
                    color="ok"
                    label={t('home.online')}
                    classes={styles => {
                      return {
                        ...styles,
                        label: classNames(styles.label, style.override, style.labelGraphOnline),
                      }
                    }}
                  />
                </span>
                <span className={classNames(style.block, style.value)}>
                  {formatNumber(data.locations.currentOnline)}
                </span>
              </div>
              <div className={style.stats}>
                <span className={style.icon}>
                  <Status
                    color="#999eff"
                    label={t('home.offline')}
                    classes={styles => {
                      return {
                        ...styles,
                        label: classNames(styles.label, style.override, style.labelGraphOffline),
                      }
                    }}
                  />
                </span>
                <span className={classNames(style.block, style.value)}>
                  {formatNumber(data.locations.currentOffline)}
                </span>
              </div>
            </div>
            <div className={style.graphWrapper}>
              <label className={style.graphLabel}>{`${t('common.months.thisMonth')}: ${t(
                `common.months.${getMonth()}`,
              )}`}</label>
              <BarChart
                classes={prev => ({
                  ...prev,
                  tooltip: classNames(prev.tooltip, style.tooltipOverride),
                })}
                data={currentMonthActiveHouseholds.map(item => ({
                  name: moment(item.ts).format('DD MMM'),
                  values: [item.val],
                }))}
                customYAxisTickFormatter={value => formatNumber(value)}
                tooltipValueFormatter={item => item.value.toLocaleString()}
                invertHoverEffect
                config={{
                  yAxisWidth: 42,
                  minHeight: 100,
                  showPercentageInGroup: false,
                  showPercentageOnTooltip: false,
                  xAxisLabelAngle: 0,
                  xAxisTickInterval: getXAxisTickInterval(currentMonthActiveHouseholds.length),
                  barSize: 6,
                }}
                groups={[
                  {
                    key: 'locations',
                    label: t('home.locations'),
                    color: '#555',
                    stroke: '#555',
                  },
                ]}
                placeHolderText={{
                  emptyHeader: t('common.noData'),
                  emptySubText: t('common.noDataSubtext'),
                  errorHeader: t('errors.error'),
                  errorSubText: t('errors.somethingWentWrong'),
                }}
              />
            </div>
            {!data.monthly.locations.hidden && (
              <div className={style.graphWrapper}>
                <label className={style.graphLabel}>
                  {t('home.graphPastNMonths', { count: data.monthly.locations.online.length })}
                </label>
                <LineChart
                  statusVariant="legend"
                  data={data.monthly.locations.online.map((item, index) => {
                    return {
                      xAxisLabel: item.month,
                      online: item.value,
                      offline: data.monthly.locations.offline[index]?.value,
                    }
                  })}
                  tickFormatter={value => formatNumber(value)}
                  height={100}
                  groups={[
                    {
                      key: 'online',
                      label: t('home.online'),
                      stroke: '#1fdc90',
                    },
                    {
                      key: 'offline',
                      label: t('home.offline'),
                      stroke: '#999eff',
                    },
                  ]}
                  legendPosition="none"
                  showFirstElementOnXAxis
                />
              </div>
            )}
          </Paper>
        </div>
        <Divider orientation={'vertical'} isFlex />
        <div className={style.col}>
          <Paper
            title={t('home.nodes')}
            name="nodes"
            icon={<Pod />}
            toggle={toggle}
            isMobile={isMobile}
            isExpanded={data.nodes.isExpanded}
          >
            <div className={style.paperContentWrapper}>
              <div className={style.stats}>
                <span className={style.icon}>
                  <Status
                    color="ok"
                    label={t('home.online')}
                    classes={styles => {
                      return {
                        ...styles,
                        label: classNames(styles.label, style.override, style.labelGraphOnline),
                      }
                    }}
                  />
                </span>
                <span className={classNames(style.block, style.value)}>
                  {formatNumber(data.nodes.currentOnline)}
                </span>
              </div>
              <div className={style.stats}>
                <span className={style.icon}>
                  <Status
                    color="#999eff"
                    label={t('home.offline')}
                    classes={styles => {
                      return {
                        ...styles,
                        label: classNames(styles.label, style.override, style.labelGraphOffline),
                      }
                    }}
                  />
                </span>
                <span className={classNames(style.block, style.value)}>
                  {formatNumber(data.nodes.currentOffline)}
                </span>
              </div>
            </div>
            <div className={style.graphWrapper}>
              <label className={style.graphLabel}>{`${t('common.months.thisMonth')}: ${t(
                `common.months.${getMonth()}`,
              )}`}</label>
              <BarChart
                data={currentMonthActiveNodes.map(item => ({
                  name: moment(item.ts).format('DD MMM'),
                  values: [item.val],
                }))}
                classes={prev => ({
                  ...prev,
                  tooltip: classNames(prev.tooltip, style.tooltipOverride),
                })}
                invertHoverEffect
                customYAxisTickFormatter={value => formatNumber(value)}
                tooltipValueFormatter={item => item.value.toLocaleString()}
                config={{
                  yAxisWidth: 42,
                  minHeight: 100,
                  showPercentageInGroup: false,
                  showPercentageOnTooltip: false,
                  xAxisLabelAngle: 0,
                  xAxisTickInterval: getXAxisTickInterval(currentMonthActiveNodes.length),
                  barSize: 6,
                }}
                groups={[
                  {
                    key: '0',
                    label: t('home.nodes'),
                    color: '#555',
                    stroke: '#555',
                  },
                ]}
                placeHolderText={{
                  emptyHeader: t('common.noData'),
                  emptySubText: t('common.noDataSubtext'),
                  errorHeader: t('errors.error'),
                  errorSubText: t('errors.somethingWentWrong'),
                }}
              />
            </div>
            {!data.monthly.nodes.hidden && (
              <div className={style.graphWrapper}>
                <label className={style.graphLabel}>
                  {t('home.graphPastNMonths', { count: data.monthly.nodes.online.length })}
                </label>
                <LineChart
                  statusVariant="legend"
                  data={data.monthly.nodes.online.map((item, index) => {
                    return {
                      xAxisLabel: item.month,
                      online: item.value,
                      offline: data.monthly.nodes.offline[index]?.value,
                    }
                  })}
                  tickFormatter={value => formatNumber(value)}
                  height={100}
                  groups={[
                    {
                      key: 'online',
                      label: t('home.online'),
                      stroke: '#1fdc90',
                    },
                    {
                      key: 'offline',
                      label: t('home.offline'),
                      stroke: '#999eff',
                    },
                  ]}
                  legendPosition="none"
                  showFirstElementOnXAxis
                />
              </div>
            )}
          </Paper>
        </div>
        <Divider orientation={'vertical'} isFlex />
        <div className={style.col}>
          <Paper
            title={t('home.devices')}
            name="devices"
            icon={<Mobile />}
            toggle={toggle}
            isMobile={isMobile}
            isExpanded={data.devices.isExpanded}
          >
            <div className={style.paperContentWrapper}>
              <div className={style.stats}>
                <span className={style.icon}>
                  <Status
                    color="ok"
                    label={t('home.online')}
                    classes={styles => {
                      return {
                        ...styles,
                        label: classNames(styles.label, style.override, style.labelGraphOnline),
                      }
                    }}
                  />
                </span>
                <span className={classNames(style.block, style.value)}>
                  {formatNumber(data.devices.currentOnline)}
                </span>
              </div>
              {/* {data.devices.currentOffline && (
                <div className={style.stats}>
                  <span className={style.icon}>
                    <Status
                      color="#999eff"
                      label={t('home.offline')}
                      classes={styles => {
                        return {
                          ...styles,
                          label: classNames(styles.label, style.override, style.labelGraphOffline),
                        }
                      }}
                    />
                  </span>
                  <span className={classNames(style.block, style.value)}>
                    {formatNumber(data.devices.currentOffline)}
                  </span>
                </div>
              )} */}
            </div>
            <div className={style.graphWrapper}>
              <label className={style.graphLabel}>{`${t('common.months.thisMonth')}: ${t(
                `common.months.${getMonth()}`,
              )}`}</label>
              <BarChart
                data={currentMonthActiveDevices.map(item => ({
                  name: moment(item.ts).format('DD MMM'),
                  values: [item.val],
                }))}
                classes={prev => ({
                  ...prev,
                  tooltip: classNames(prev.tooltip, style.tooltipOverride),
                })}
                invertHoverEffect
                customYAxisTickFormatter={value => formatNumber(value)}
                tooltipValueFormatter={item => item.value.toLocaleString()}
                config={{
                  yAxisWidth: 42,
                  minHeight: 100,
                  showPercentageInGroup: false,
                  showPercentageOnTooltip: false,
                  xAxisLabelAngle: 0,
                  xAxisTickInterval: getXAxisTickInterval(currentMonthActiveDevices.length),
                  barSize: 6,
                }}
                groups={[
                  {
                    key: '0',
                    label: t('home.devices'),
                    color: '#555',
                    stroke: '#555',
                  },
                ]}
                placeHolderText={{
                  emptyHeader: t('common.noData'),
                  emptySubText: t('common.noDataSubtext'),
                  errorHeader: t('errors.error'),
                  errorSubText: t('errors.somethingWentWrong'),
                }}
              />
            </div>
            {!data.monthly.devices.hidden && (
              <div className={style.graphWrapper}>
                <label className={style.graphLabel}>
                  {t('home.graphPastNMonths', { count: data.monthly.devices.online.length })}
                </label>
                <LineChart
                  statusVariant="legend"
                  data={data.monthly.devices.online.map((item, index) => {
                    return {
                      xAxisLabel: item.month,
                      online: item.value,
                      offline: data.monthly.devices.offline[index]?.value,
                    }
                  })}
                  height={100}
                  tickFormatter={value => formatNumber(value)}
                  groups={[
                    {
                      key: 'online',
                      label: t('home.online'),
                      stroke: '#1fdc90',
                    },
                    {
                      key: 'offline',
                      label: t('home.offline'),
                      stroke: '#999eff',
                    },
                  ]}
                  legendPosition="none"
                  showFirstElementOnXAxis
                />
              </div>
            )}
          </Paper>
        </div>
      </div>
    )
  }

  const getActions = () => {
    if (noHomeData) {
      return (
        <Status
          color="warning"
          label="Incomplete"
          classes={styles => {
            return {
              ...styles,
              label: classNames(styles.label, style.override, style.labelIncomplete),
            }
          }}
        />
      )
    }
  }

  return (
    <CollapseCard
      title={<Heading size="xl">{t('home.networkTitle')}</Heading>}
      actions={[getActions()]}
    >
      <div className={style.container}>
        <div>
          {!isTrialExpiredOrTerminated && !initialOpen && (
            <InitialOpeningHomes onClose={handleInitialOpen} />
          )}

          {!isTrialExpiredOrTerminated && noAccess ? (
            <EmptyHomesOrError
              errorMessage={t('home.errorNoAccess')}
              mailAddress={supportEmails.PARTNER_SUPPORT}
            />
          ) : !isTrialExpiredOrTerminated && data.emptyHomes ? (
            <EmptyHomesOrError serverError={serverError} mailAddress={mailAddress} />
          ) : (
            renderHomeData()
          )}
        </div>
        <div className={style.colSpan2}>
          <p className={style.disclaimer}>{t('home.disclaimer')}</p>
        </div>
      </div>
    </CollapseCard>
  )
})
